import { Box } from "@chakra-ui/react";
import { GoogleMap, Marker, useJsApiLoader } from "@react-google-maps/api";
import { UserAddress } from "../../auth/AuthContext";
import { useCustomTheme } from "../../contexts/CustomThemeContext";
import { useEffect, useMemo, useRef } from "react";

const cardsBorderRadii ="8px";

interface LocationPoint {
    lat: number;
    lng: number;
    label?: string;
}

interface Props {
    address?: UserAddress;
    origin?: LocationPoint | null;
}

const mapContainerStyle = {
    width: "100%",
    height: "150px",
    borderRadius: cardsBorderRadii,
};

const darkMapStyles: google.maps.MapTypeStyle[] = [
    { elementType: "geometry", stylers: [{ color: "#1f2937" }] },
    { elementType: "labels.text.stroke", stylers: [{ color: "#111827" }] },
    { elementType: "labels.text.fill", stylers: [{ color: "#9ca3af" }] },
    {
        featureType: "administrative.locality",
        elementType: "labels.text.fill",
        stylers: [{ color: "#d1d5db" }],
    },
    {
        featureType: "poi",
        elementType: "labels.text.fill",
        stylers: [{ color: "#9ca3af" }],
    },
    {
        featureType: "poi.park",
        elementType: "geometry",
        stylers: [{ color: "#1f3a2e" }],
    },
    {
        featureType: "road",
        elementType: "geometry",
        stylers: [{ color: "#374151" }],
    },
    {
        featureType: "road",
        elementType: "geometry.stroke",
        stylers: [{ color: "#111827" }],
    },
    {
        featureType: "road",
        elementType: "labels.text.fill",
        stylers: [{ color: "#cbd5e1" }],
    },
    {
        featureType: "water",
        elementType: "geometry",
        stylers: [{ color: "#0f172a" }],
    },
    {
        featureType: "water",
        elementType: "labels.text.fill",
        stylers: [{ color: "#94a3b8" }],
    },
];

const ActiveAddressMap: React.FC<Props> = ({ address, origin }) => {
    const { theme } = useCustomTheme();
    const mapRef = useRef<google.maps.Map | null>(null);

    const { isLoaded } = useJsApiLoader({
        googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY!,
        libraries: ["places"],
    });

    const mapStyles = useMemo(() => {
        return theme.name === "dark" ? darkMapStyles : [];
    }, [theme.name]);

    const destination = useMemo(() => {
        if (!address) return null;
        return {
            lat: address.latitude,
            lng: address.longitude,
            label: address.label,
        };
    }, [address]);

    useEffect(() => {
        if (!isLoaded || !mapRef.current) return;
        if (!destination && !origin) return;

        const map = mapRef.current;

        if (origin && destination) {
            const bounds = new window.google.maps.LatLngBounds();
            bounds.extend({ lat: origin.lat, lng: origin.lng });
            bounds.extend({ lat: destination.lat, lng: destination.lng });
            map.fitBounds(bounds);

            const listener = window.google.maps.event.addListenerOnce(
                map,
                "bounds_changed",
                () => {
                    if ((map.getZoom() || 0) > 14) {
                        map.setZoom(14);
                    }
                },
            );

            return () => {
                window.google.maps.event.removeListener(listener);
            };
        }

        if (destination) {
            map.panTo({ lat: destination.lat, lng: destination.lng });
            map.setZoom(15);
            return;
        }

        if (origin) {
            map.panTo({ lat: origin.lat, lng: origin.lng });
            map.setZoom(15);
        }
    }, [isLoaded, origin, destination]);

    if (!isLoaded || (!destination && !origin)) return null;

    const initialCenter = destination
        ? { lat: destination.lat, lng: destination.lng }
        : origin
            ? { lat: origin.lat, lng: origin.lng }
            : { lat: -13.9626, lng: 33.7741 };

    return (
        <Box
            mb={6}
            overflow="hidden"
            borderRadius={cardsBorderRadii}
            border="1px solid"
            borderColor={theme.border}
            boxShadow={theme.shadowSm}
            bg={theme.surfaceAlt}
        >
            <GoogleMap
                mapContainerStyle={mapContainerStyle}
                center={initialCenter}
                zoom={15}
                onLoad={(map) => {
                    mapRef.current = map;
                }}
                options={{
                    disableDefaultUI: true,
                    zoomControl: true,
                    styles: mapStyles,
                    backgroundColor: theme.surfaceAlt,
                }}
            >
                {origin && (
                    <Marker
                        position={{ lat: origin.lat, lng: origin.lng }}
                        label="W"
                    />
                )}

                {destination && (
                    <Marker
                        position={{ lat: destination.lat, lng: destination.lng }}
                        label="D"
                    />
                )}
            </GoogleMap>
        </Box>
    );
};

export default ActiveAddressMap;